import { FC, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '../../atoms';
import cn from 'classnames';
import { CustomDropdown } from '../../molecules';
import { getRoleSeparation } from '../../../utils/Helpers/role-helper';
import { Role } from '../../../store/contextAPI/role/dto/response/role.dto';
import { BasicInfo } from '../../../utils/types/types';
import styles from './AddRoleModal.module.css';

interface AddRoleModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  roles: Role[];
  onSelect: (role: Role) => void;
  addRole: () => void;
  landscapes: BasicInfo[];
  buyers: BasicInfo[];
  conveners: BasicInfo[];
  consortiums: BasicInfo[];
  onSelectLandscape: (landscape: BasicInfo) => void;
  onSelectBuyer: (buyer: BasicInfo) => void;
}

interface DropDownItem {
  eventKey: string;
  label: string;
}

const AddRoleModal: FC<AddRoleModalProps> = ({
  show,
  setShow,
  roles,
  onSelect,
  addRole,
  buyers,
  conveners,
  consortiums,
  landscapes,
  onSelectLandscape,
  onSelectBuyer,
}) => {
  const roleItems: DropDownItem[] = roles.map((role) => {
    return {
      eventKey: role.id,
      label: getRoleSeparation(role.name),
    };
  });
  const [activeRole, setActiveRole] = useState<Role['name'] | null>(null);

  const landscapeItems: DropDownItem[] = landscapes.map((landscape) => {
    return {
      eventKey: landscape.id,
      label: landscape.name,
    };
  });

  const buyerItems: DropDownItem[] = buyers.map((buyer) => {
    return {
      eventKey: buyer.id,
      label: buyer.name ?? 'No name provided',
    };
  });

  const convenerItems: DropDownItem[] = conveners.map((convener) => {
    return {
      eventKey: convener.id,
      label: convener.name ?? 'No name provided',
    };
  });

  const consortiumItems: DropDownItem[] = consortiums.map((consortium) => {
    return {
      eventKey: consortium.id,
      label: consortium.name ?? 'No name provided',
    };
  });

  const onRoleSelected = (dropdownItem: DropDownItem) => {
    const selectedRole: Role = roles.find(
      (role) => role.id === dropdownItem.eventKey
    )!;

    setActiveRole(selectedRole.name);

    onSelect(selectedRole);
  };

  return (
    <Modal centered show={show} onHide={() => setShow(false)}>
      <div className={cn(styles.modal)}>
        <Modal.Body className={cn(styles.modalBody)}>
          <div>
            <Form.Group className="mb-3">
              <Form.Label>Select Role:</Form.Label>
              <CustomDropdown
                initialTitle={'Please select a Role'}
                items={roleItems}
                onSelectOption={onRoleSelected}
              />
            </Form.Group>
          </div>

          {activeRole === 'BuyerManager' && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Apply Role to:</Form.Label>
                <CustomDropdown
                  initialTitle={'Please select a Buyer'}
                  items={buyerItems}
                  onSelectOption={(dropdownItem: DropDownItem) => {
                    const selectedBuyer = buyers.find(
                      (b) => b.id === dropdownItem.eventKey
                    )!;
                    onSelectBuyer(selectedBuyer);
                  }}
                />
              </Form.Group>
            </div>
          )}

          {activeRole === 'ConvenerManager' && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Apply Role to:</Form.Label>
                <CustomDropdown
                  initialTitle={'Please select a Convener'}
                  items={convenerItems}
                  onSelectOption={(dropdownItem: DropDownItem) => {
                    const selectedConvener = conveners.find(
                      (c) => c.id === dropdownItem.eventKey
                    )!;
                    onSelectBuyer(selectedConvener);
                  }}
                />
              </Form.Group>
            </div>
          )}

          {activeRole === 'ConsortiumManager' && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Apply Role to:</Form.Label>
                <CustomDropdown
                  initialTitle={'Please select a Consortium'}
                  items={consortiumItems}
                  onSelectOption={(dropdownItem: DropDownItem) => {
                    const selectedConsortium = consortiums.find(
                      (c) => c.id === dropdownItem.eventKey
                    )!;
                    onSelectBuyer(selectedConsortium);
                  }}
                />
              </Form.Group>
            </div>
          )}

          {activeRole === 'LandscapeInitiativeManager' && (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Apply Role to:</Form.Label>
                <CustomDropdown
                  initialTitle={'Please select a Landscape'}
                  items={landscapeItems}
                  onSelectOption={(dropdownItem: DropDownItem) => {
                    const selectedLandscape = landscapes.find(
                      (l) => l.id === dropdownItem.eventKey
                    )!;
                    onSelectLandscape(selectedLandscape);
                  }}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={cn(styles.modalButtons)}>
          <Button
            className={cn(styles.button)}
            variant="dark"
            onClick={addRole}
          >
            Save
          </Button>
          <Button
            className={cn(styles.cancelButton)}
            variant="light"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default AddRoleModal;
